import { useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AttachFileOutlined, Close, SendOutlined } from "@mui/icons-material";
import { API_URL } from "../../apiConfig";

const QuoteModal = ({ open, onClose, productName, selectedOptions, file }) => {
  const [formValues, setFormValues] = useState({
    fullName: "",
    emailAddress: "",
    message: "",
  });
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  const buildDefaultMessage = () => {
    let msg = `Jeg ønsker tilbud på: ${productName}`;
    if (selectedOptions) {
      const parts = Object.entries(selectedOptions)
        .filter(([, val]) => val)
        .map(([key, val]) => `${key}: ${val}`);
      if (parts.length) msg += `\n${parts.join(", ")}`;
    }
    return msg;
  };

  const handleOpen = () => {
    setFormValues({ fullName: "", emailAddress: "", message: buildDefaultMessage() });
    setStatus(null);
    setErrorMsg("");
  };

  const handleFieldChange = (field) => (e) => {
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("fullName", formValues.fullName);
      formData.append("emailAddress", formValues.emailAddress);
      formData.append("message", formValues.message);
      formData.append("productName", productName);
      if (file) formData.append("file", file);

      await axios.post(`${API_URL}/quote`, formData);
      setStatus("success");
    } catch (err) {
      const msg =
        err.response?.data?.message || "Kunne ikke sende forespørsel. Prøv igjen senere.";
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  const handleClose = () => {
    if (status === "loading") return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onEnter: handleOpen }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        Be om tilbud
        <IconButton
          onClick={handleClose}
          disabled={status === "loading"}
          sx={{ position: "absolute", right: 12, top: 12 }}
          aria-label="Lukk"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ opacity: 0.75, mb: 2.5 }}>
          Fyll ut skjemaet nedenfor, så tar vi kontakt med deg.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2.2 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
              Navn
            </Typography>
            <TextField
              fullWidth
              placeholder="Ditt navn"
              value={formValues.fullName}
              onChange={handleFieldChange("fullName")}
              disabled={status === "loading" || status === "success"}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
              E-post
            </Typography>
            <TextField
              fullWidth
              placeholder="din@epost.no"
              value={formValues.emailAddress}
              onChange={handleFieldChange("emailAddress")}
              disabled={status === "loading" || status === "success"}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8 }}>
              Melding
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={formValues.message}
              onChange={handleFieldChange("message")}
              disabled={status === "loading" || status === "success"}
            />
          </Box>

          {file && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                backgroundColor: "rgba(0,0,0,0.04)",
              }}
            >
              <AttachFileOutlined sx={{ fontSize: 18, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Vedlegg: {file.name}
              </Typography>
            </Box>
          )}

          {status === "success" && (
            <Alert severity="success">
              Forespørselen er sendt! Vi tar kontakt snart.
            </Alert>
          )}
          {status === "error" && <Alert severity="error">{errorMsg}</Alert>}

          {status !== "success" && (
            <Button
              type="submit"
              disabled={status === "loading"}
              startIcon={
                status === "loading" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendOutlined />
                )
              }
              sx={{
                backgroundColor: "secondary.main",
                borderRadius: 3,
                py: 1.4,
                fontWeight: 900,
                textTransform: "none",
                color: "rgba(0,0,0,0.9)",
              }}
            >
              {status === "loading" ? "Sender..." : "Send forespørsel"}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
